/**
 * Conversation state for the Agapaé AI assistant surfaces (drawer + planner).
 *
 * Owns hydration (GET /conversation — always the event's single ACTIVE
 * conversation), sending a turn (SSE streaming), "New chat" (reset), and
 * resuming a past conversation from history (activate). One in-flight reply
 * at a time — send() is a no-op while streaming (the BE enforces the same
 * with a 409).
 *
 * Error semantics mirror what the BE persisted:
 * - PRE-stream failure (validation / rate limit / 503 / 409): the server
 *   never stored the user message → the optimistic bubble is rolled back
 *   and the text is handed back for the composer to restore.
 * - MID-stream failure: the server persisted the user turn (and any partial
 *   reply) → both stay in the thread; only the error notice shows.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  activateAssistantConversation,
  AssistantMessageDto,
  getAssistantConversation,
  isBudgetExhaustedError,
  resetAssistantConversation,
  streamAssistantMessage,
  ToolActivity,
  WriteReceipt,
} from 'services/api/assistant';

export type AssistantChatStatus = 'loading' | 'idle' | 'streaming' | 'error';

/** A message plus FE-only view flags that aren't carried on the wire. */
export interface ChatMessage extends AssistantMessageDto {
  /** Reply hit the output-token cap (stop_reason "max_tokens") — show a hint. */
  truncated?: boolean;
}

interface UseAssistantChatOptions {
  eventId: string | undefined;
  /** Hydrate lazily — pass true once the surface is actually shown. */
  enabled: boolean;
}

export function useAssistantChat({ eventId, enabled }: UseAssistantChatOptions) {
  const { i18n } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingText, setStreamingText] = useState('');
  /** Live "checking your data / creating the task" state while a tool runs. */
  const [toolActivity, setToolActivity] = useState<ToolActivity | null>(null);
  const [status, setStatus] = useState<AssistantChatStatus>('loading');
  const [error, setError] = useState<unknown>(null);
  /** Set on pre-stream failures so the composer can restore the typed text. */
  const [restoredDraft, setRestoredDraft] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const hydratedRef = useRef(false);
  // Whether the last hydration attempt ended in failure, so reopening the
  // surface can re-arm `hydratedRef` and load the thread again (UX-5). A send
  // failure must NOT re-hydrate — the thread is already on screen.
  const hydrationFailedRef = useRef(false);
  // Synchronous re-entrancy guard: `status` is async state, so a same-tick
  // double-submit (holding Enter) can read 'idle' twice — this ref blocks the
  // second send before it mints a colliding bubble (UX-2).
  const sendingRef = useRef(false);
  // Monotonic suffix so two bubbles created in the same millisecond never share
  // an id — a colliding id let one send's rollback delete the other's bubble (UX-2).
  const idSeqRef = useRef(0);
  // Bumped by retry() to re-run the hydration effect after a failed load (UX-5).
  const [reloadNonce, setReloadNonce] = useState(0);

  const nextLocalId = () => `local-${Date.now()}-${idSeqRef.current++}`;

  // Hydrate once when the surface first opens — always the event's single
  // ACTIVE conversation, so drawer and planner show the same thread.
  useEffect(() => {
    if (!enabled || !eventId || hydratedRef.current) return;
    hydratedRef.current = true;
    let cancelled = false;
    let settled = false;
    (async () => {
      try {
        const data = await getAssistantConversation(eventId);
        if (!cancelled) {
          hydrationFailedRef.current = false;
          setMessages(data.messages);
          setStatus('idle');
        }
      } catch (err) {
        if (!cancelled) {
          hydrationFailedRef.current = true;
          setError(err);
          setStatus('error');
        }
      } finally {
        settled = true;
      }
    })();
    return () => {
      cancelled = true;
      // If we're torn down before the request settles — StrictMode's
      // mount→unmount→remount in dev, or a fast dep change — re-arm the guard
      // so the remount hydrates again. Otherwise the cancelled first run leaves
      // status stuck on 'loading' (the remount would early-return on the ref).
      if (!settled) hydratedRef.current = false;
    };
  }, [enabled, eventId, reloadNonce]);

  // Mirror of `error`, so the reopen reset below can read the last failure
  // without taking `error` as a dependency (which would re-run it mid-session).
  const lastErrorRef = useRef<unknown>(null);
  useEffect(() => {
    lastErrorRef.current = error;
  }, [error]);

  // Reset a PREVIOUS session's failure when the surface is (re)opened. The
  // owning component stays mounted while closed (the drawer just renders
  // `open={false}`), so nothing else clears this state: without it a blip that
  // has long since passed still greets the couple as a banner on the next open
  // (UX-3), and a hydration that never landed never reloads — leaving the
  // thread stuck on the empty home view until a full page reload (UX-5).
  //
  // Budget exhaustion is deliberately preserved: it's durable state, not a
  // blip. Clearing it would drop the notice and let the couple type a doomed
  // message just to re-learn it (UX-4).
  useEffect(() => {
    if (!enabled) return;
    const staleError = lastErrorRef.current;
    if (staleError == null || isBudgetExhaustedError(staleError)) return;
    setError(null);
    if (hydrationFailedRef.current) {
      // The thread never loaded — re-arm the once-only guard and reload it.
      // Bumping the nonce (rather than only flipping the ref) re-runs the
      // hydration effect regardless of effect ordering within this commit.
      hydrationFailedRef.current = false;
      hydratedRef.current = false;
      setStatus('loading');
      setReloadNonce((n) => n + 1);
    } else {
      setStatus((prev) => (prev === 'error' ? 'idle' : prev));
    }
  }, [enabled]);

  // Abort any in-flight stream on unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  const send = useCallback(
    async (content: string) => {
      const text = content.trim();
      if (!text || !eventId) return;
      if (status === 'streaming' || sendingRef.current) return;
      sendingRef.current = true;

      setError(null);
      setRestoredDraft(null);

      // Optimistic user bubble (unique temp id — a colliding id would let the
      // pre-flush rollback below delete another in-flight bubble).
      const tempId = nextLocalId();
      const optimistic: AssistantMessageDto = {
        id: tempId,
        role: 'user',
        content: text,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimistic]);
      setStreamingText('');
      setStatus('streaming');

      const controller = new AbortController();
      abortRef.current = controller;
      let accumulated = '';
      // What the writes on this turn actually did — the authoritative record
      // shown under the reply, from the tools' own results rather than the text.
      const receipts: WriteReceipt[] = [];
      // Flips once the server responds 200 and the stream opens — by then the
      // user turn is persisted server-side (see streamAssistantMessage).
      let streamStarted = false;

      try {
        const result = await streamAssistantMessage({
          eventId,
          content: text,
          locale: i18n.language.slice(0, 2),
          signal: controller.signal,
          onStreamStart: () => {
            streamStarted = true;
          },
          onDelta: (delta) => {
            accumulated += delta;
            setToolActivity(null);
            setStreamingText((prev) => prev + delta);
          },
          onTool: (activity) => setToolActivity(activity),
          onToolResult: (outcome) => {
            // A refused write still emitted a `tool` event, so the label reads
            // "Applying the seating plan…" for something that never happened —
            // and lingers until the next delta. Clear it the moment it fails.
            if (!outcome.ok) setToolActivity(null);
            // Writes only: a lookup is not an action. Collected here because the
            // bubble below is built from `accumulated` and never re-fetched — the
            // BE persists the identical rows, so a refresh renders the same thing.
            if (outcome.kind === 'write') {
              receipts.push({
                tool: outcome.name,
                status: outcome.status,
                ok: outcome.ok,
                params: outcome.params,
              });
            }
          },
        });
        setMessages((prev) => [
          ...prev,
          {
            id: result.messageId ?? nextLocalId(),
            role: 'assistant',
            content: accumulated,
            createdAt: new Date().toISOString(),
            truncated: result.stopReason === 'max_tokens',
            receipts: receipts.length ? receipts : undefined,
          },
        ]);
        setStreamingText('');
        setStatus('idle');
      } catch (err) {
        if ((err as DOMException)?.name === 'AbortError') {
          // Drawer closed / unmounted mid-reply — keep whatever streamed in
          // as a message (the BE persisted the partial too).
          if (accumulated) {
            setMessages((prev) => [
              ...prev,
              {
                id: nextLocalId(),
                role: 'assistant',
                content: accumulated,
                createdAt: new Date().toISOString(),
              },
            ]);
          }
          setStreamingText('');
          setStatus('idle');
          return;
        }

        if (streamStarted) {
          // Post-flush failure: the server persisted the user turn. Keep it,
          // plus any partial reply. A refusal/empty-content error carries no
          // partial, so the user turn simply stands with no assistant bubble —
          // never a silently vanished message.
          if (accumulated) {
            setMessages((prev) => [
              ...prev,
              {
                id: nextLocalId(),
                role: 'assistant',
                content: accumulated,
                createdAt: new Date().toISOString(),
              },
            ]);
          }
        } else {
          // Pre-flush failure (validation / rate limit / plan gate / 409 / 503):
          // server stored nothing — roll back the optimistic bubble and hand
          // the text back to the composer.
          setMessages((prev) => prev.filter((m) => m.id !== tempId));
          setRestoredDraft(text);
        }
        setStreamingText('');
        setError(err);
        setStatus('error');
      } finally {
        sendingRef.current = false;
        setToolActivity(null);
        abortRef.current = null;
      }
    },
    [eventId, status, i18n.language],
  );

  const reset = useCallback(async (): Promise<boolean> => {
    if (!eventId || status === 'streaming') return false;
    try {
      await resetAssistantConversation(eventId);
      setMessages([]);
      setError(null);
      setStatus('idle');
      return true;
    } catch (err) {
      setError(err);
      setStatus('error');
      return false;
    }
  }, [eventId, status]);

  /**
   * Resume a past conversation from history. The current thread stays on
   * screen until the server confirms (no blank flash); on success the swapped
   * thread replaces it. No-op while streaming (the BE 409s regardless).
   */
  const activate = useCallback(
    async (conversationId: string): Promise<boolean> => {
      if (!eventId || status === 'streaming') return false;
      try {
        const data = await activateAssistantConversation(eventId, conversationId);
        setMessages(data.messages);
        setError(null);
        setRestoredDraft(null);
        setStatus('idle');
        return true;
      } catch (err) {
        setError(err);
        setStatus('error');
        return false;
      }
    },
    [eventId, status],
  );

  const clearError = useCallback(() => {
    setError(null);
    if (status === 'error') setStatus('idle');
  }, [status]);

  // Re-run hydration after a failed initial load, so a transient network error
  // doesn't leave the drawer permanently stuck (and stranding a pending seed) (UX-5).
  const retry = useCallback(() => {
    if (!eventId || status === 'streaming') return;
    hydratedRef.current = false;
    setError(null);
    setStatus('loading');
    setReloadNonce((n) => n + 1);
  }, [eventId, status]);

  return {
    messages,
    streamingText,
    toolActivity,
    status,
    error,
    restoredDraft,
    send,
    reset,
    activate,
    clearError,
    retry,
  };
}
